import { useState } from "react";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { subscribeWaitlist } from "../lib/api";

/**
 * FoundingSpotClaim — homepage founding-member capture.
 *
 * Honest by design: no scarcity, no progress bar, no "of 100". A single
 * truthful perk line, the live confirmed-member count, and a first-name +
 * email form. Cream canvas to match the page (not the old dark-green block).
 */
export default function FoundingSpotClaim({ founding }) {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [err, setErr] = useState("");
  const [memberNo, setMemberNo] = useState(null);

  const count = typeof founding?.count === "number"
    ? founding.count
    : (typeof founding?.claimed === "number" ? founding.claimed : null);
  const soFar = count === null
    ? ""
    : count === 0
      ? "Be the first."
      : `${count.toLocaleString("en-US")} so far.`;

  const submit = async (e) => {
    e.preventDefault();
    if (status === "loading") return;
    if (!firstName.trim()) { setErr("Please add your first name."); return; }
    if (!email.trim()) { setErr("Please add your email."); return; }
    setStatus("loading");
    setErr("");
    try {
      const res = await subscribeWaitlist({
        email: email.trim().toLowerCase(),
        first_name: firstName.trim(),
        source: "founding_home",
      });
      setMemberNo(res?.founding_number ?? null);
      setStatus("done");
    } catch (e2) {
      setStatus("error");
      setErr(e2?.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  return (
    <section
      id="founding"
      data-testid="founding-spot-claim"
      className="relative bg-cream-50 py-16 md:py-20 border-t border-surface-line"
    >
      <div className="max-w-xl mx-auto px-5 sm:px-6 text-center">
        <p className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[#a87a32] mb-3">
          Founding member
        </p>
        <h2
          data-testid="founding-headline"
          className="text-[30px] sm:text-[38px] font-extrabold tracking-[-0.02em] leading-[1.08] text-ink"
        >
          Become a founding member
        </h2>
        <p data-testid="founding-subline" className="mt-3 text-[15px] text-ink-soft leading-relaxed">
          Pro free for 12 months when payments launch.{" "}
          <span className="text-ink font-semibold" data-testid="founding-count">{soFar}</span>
        </p>

        {status === "done" ? (
          <div
            data-testid="founding-success"
            className="mt-8 inline-flex items-center gap-2 rounded-xl bg-[#0e6b34]/10 border border-[#0e6b34]/25 px-5 py-3.5 text-[14.5px] font-semibold text-[#0e6b34]"
          >
            <Check className="w-4 h-4 shrink-0" />
            {memberNo
              ? `You're founding member #${memberNo} — check your email to confirm.`
              : "You're on the list — check your email to confirm."}
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 flex flex-col gap-2.5 max-w-md mx-auto text-left">
            <div className="flex flex-col sm:flex-row gap-2.5">
              <input
                type="text"
                required
                maxLength={30}
                data-testid="founding-firstname-input"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
                className="flex-1 h-12 px-4 rounded-xl bg-white border border-surface-line text-[15px] text-ink placeholder:text-ink-light focus:outline-none focus:ring-4 focus:ring-cream-100 focus:border-ink-mute transition-all"
              />
              <input
                type="email"
                required
                data-testid="founding-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="flex-[1.4] h-12 px-4 rounded-xl bg-white border border-surface-line text-[15px] text-ink placeholder:text-ink-light focus:outline-none focus:ring-4 focus:ring-cream-100 focus:border-ink-mute transition-all"
              />
            </div>
            <button
              type="submit"
              data-testid="founding-claim-btn"
              disabled={status === "loading"}
              className="h-12 px-6 rounded-xl text-cream-50 text-[15px] font-bold inline-flex items-center justify-center gap-2 transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-70"
              style={{ background: "linear-gradient(135deg,#1f3d2e 0%,#2c5240 50%,#1f3d2e 100%)" }}
            >
              {status === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Claim my spot <ArrowRight className="w-4 h-4" /></>}
            </button>
            {err && <p data-testid="founding-error" className="text-[13px] text-red-600">{err}</p>}
          </form>
        )}
      </div>
    </section>
  );
}
